import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { Notifications as NotificationsSetting } from "@/commons/services/Facilities";

/**
 * ### お知らせ API 操作クラス
 */
export default class Notifications {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET Notifications
   */
  public async get(
    userStore: LoginUser,
    queryParams?: {
      startIndex: number;
      endIndex: number;
      order: string;
    }
  ): Promise<Array<NotificationsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Notifications, formatParams);
    const response = await this.commonService.executeGet<Array<NotificationsGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      queryParams,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * GET NotificationsCount
   */
  public async getCount(userStore: LoginUser): Promise<number> {
    // countの場合は末尾に「count」をつける
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Notifications, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * PATCH Notifications
   */
  public async patch(userStore: LoginUser, notificationId: number, userId: string): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      notificationId: notificationId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Notification, formatParams);

    // APIのモデルに合わせてここで送信内容を調整
    const requestBody: any = {
      userId: userId
    };

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET UnconfirmedNotificationsCount
   *
   * - パラメータでユーザーIDを渡すため、セキュリティ上の観点でPOSTリクエストになっている
   */
  public async getUnconfirmedNotificationCount(userStore: LoginUser, requestBody: UnConfirmdNotificationsPostModel): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.UnconfirmedNotificationCount, formatParams);
    const response = await this.commonService.executePost<{ count: number }>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.IGNORE, 0);

    return response.data.count;
  }

  /**
   * ### PATCH NotificationSettings
   */
  public async patchNotificationSettings(userStore: LoginUser, requestBody: NotificationSettingsPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.NotificationSettings, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

/**
 * お知らせ
 */
export interface NotificationsGetModel {
  /** 一意なID */
  id: number;
  /** お知らせ内容(HTML) */
  text: string;
  /** タイトル */
  title: string;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** お知らせ掲載開始日時(YYYYMMDDhhmmssSSS) */
  startPublish: number;
  /** お知らせ掲載終了日時(YYYYMMDDhhmmssSSS) */
  endPublish: number;
  /** 確認が必須かどうかのフラグ */
  required: boolean;
  /** 作成日時(YYYYMMDDhhmmssSSS) */
  created: number;
  /** 更新日時(YYYYMMDDhhmmssSSS) */
  updated: number;
}

export interface UnConfirmdNotificationsPostModel {
  userId: string;
}

export interface NotificationSettingsPatchModel {
  /** 家族・施設関係者のLINEbot操作の通知設定 */
  notifications: NotificationsSetting;
  /** 更新日時 */
  updated?: number;
}
